import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { documentService } from '../services/documentService'
import type { Document, CreateDocumentData, DocumentParty } from '../types/document'
import {
  ArrowLeftIcon,
  CheckIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import NotificationDropdown from '../components/NotificationDropdown'
import SettingsDropdown from '../components/SettingsDropdown'

interface PartyForm extends Omit<DocumentParty, 'id' | 'signed' | 'signedAt'> {
  tempId: string
  existingId?: string
}

const EditDocumentPage = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  const navigate = useNavigate()

  const [document, setDocument] = useState<Document | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: [] as string[],
    effectiveDate: '',
    expirationDate: '',
    contractValue: '',
    paymentTerms: '',
    cancellationTerms: '',
    governingLaw: '',
    jurisdiction: '',
    signatureRequired: true
  })

  const [variables, setVariables] = useState<Record<string, string>>({})
  const [parties, setParties] = useState<PartyForm[]>([])
  const [currentTag, setCurrentTag] = useState('')

  useEffect(() => {
    if (user && id) {
      loadDocument()
    }
  }, [user, id])

  const loadDocument = async () => {
    if (!user || !id) return

    setIsLoading(true)
    const result = await documentService.getDocument(id, user.id)

    if (result.success && result.document) {
      const doc = result.document
      setDocument(doc)
      
      // Populate form data
      setFormData({
        title: doc.title,
        description: doc.description || '',
        category: doc.category,
        tags: doc.tags,
        effectiveDate: doc.effectiveDate || '',
        expirationDate: doc.expirationDate || '',
        contractValue: doc.contractValue?.toString() || '',
        paymentTerms: doc.paymentTerms || '',
        cancellationTerms: doc.cancellationTerms || '',
        governingLaw: doc.governingLaw || '',
        jurisdiction: doc.jurisdiction || '',
        signatureRequired: doc.signatureRequired
      })

      setVariables(doc.variables || {})
      
      // Transform parties to form format
      setParties(doc.parties.map(party => ({
        tempId: party.id,
        existingId: party.id,
        type: party.type,
        name: party.name,
        email: party.email,
        company: party.company || '',
        role: party.role,
        address: party.address || '',
        phone: party.phone || '',
        signatureRequired: party.signatureRequired
      })))
    } else {
      showError('Error', result.error || 'Failed to load document')
      navigate('/documents')
    }
    setIsLoading(false)
  }

  const addTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }))
      setCurrentTag('')
    }
  }

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }))
  }

  const addParty = () => {
    const newParty: PartyForm = {
      tempId: Date.now().toString(),
      type: 'client',
      name: '',
      email: '',
      company: '',
      role: '',
      address: '',
      phone: '',
      signatureRequired: true
    }
    setParties([...parties, newParty])
  }

  const updateParty = (tempId: string, field: keyof PartyForm, value: string | boolean) => {
    setParties(parties.map(party => 
      party.tempId === tempId ? { ...party, [field]: value } : party
    ))
  }

  const removeParty = (tempId: string) => {
    setParties(parties.filter(party => party.tempId !== tempId))
  }

  const handleSubmit = async () => {
    if (!user || !document) return

    setIsSaving(true)
    
    try {
      const updateData: Partial<CreateDocumentData> = {
        title: formData.title,
        description: formData.description,
        tags: formData.tags,
        category: formData.category,
        effectiveDate: formData.effectiveDate || undefined,
        expirationDate: formData.expirationDate || undefined,
        contractValue: formData.contractValue ? parseFloat(formData.contractValue) : undefined,
        paymentTerms: formData.paymentTerms || undefined,
        cancellationTerms: formData.cancellationTerms || undefined,
        governingLaw: formData.governingLaw || undefined,
        jurisdiction: formData.jurisdiction || undefined,
        parties: parties.map(({ tempId, existingId, ...party }) => party),
        variables,
        signatureRequired: formData.signatureRequired,
        templateId: document.templateId,
        documentType: document.documentType
      }

      const result = await documentService.updateDocument(document.id, user.id, updateData)
      
      if (result.success) {
        showSuccess('Success', 'Document updated successfully!')
        navigate(`/documents/${document.id}`)
      } else {
        showError('Error', result.error || 'Failed to update document')
      }
    } catch (error) {
      showError('Error', 'Failed to update document')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
          <div className="floating-orb floating-orb-4"></div>
          <div className="floating-orb floating-orb-5"></div>
        </div>
        
        <AppHeader>
          <NotificationDropdown />
          <SettingsDropdown />
          <ProfileDropdown />
        </AppHeader>

        <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading document...</p>
          </div>
        </main>
      </div>
    )
  }

  if (!document) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
          <div className="floating-orb floating-orb-4"></div>
          <div className="floating-orb floating-orb-5"></div>
        </div>
        
        <AppHeader>
          <NotificationDropdown />
          <SettingsDropdown />
          <ProfileDropdown />
        </AppHeader>

        <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-white mb-2">Document Not Found</h2>
            <p className="text-gray-400 mb-6">The document you're trying to edit doesn't exist or you don't have permission to edit it.</p>
            <Link
              to="/documents"
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Back to Documents
            </Link>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader>
        <NotificationDropdown />
        <SettingsDropdown />
        <ProfileDropdown />
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center space-x-4">
            <Link
              to={`/documents/${document.id}`}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-white" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white">Edit Document</h1>
              <p className="text-gray-400">Update document details and content</p>
            </div>
          </div>
        </motion.div>

        <div className="space-y-8">
          {/* Document Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-mockup rounded-xl p-6"
          >
            <h2 className="text-xl font-semibold text-white mb-6">Document Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Document Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  placeholder="Enter document title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  placeholder="Document category"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  placeholder="Brief description of the document"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Effective Date</label>
                <input
                  type="date"
                  value={formData.effectiveDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Expiration Date</label>
                <input
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, expirationDate: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Contract Value ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.contractValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, contractValue: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Governing Law</label>
                <input
                  type="text"
                  value={formData.governingLaw}
                  onChange={(e) => setFormData(prev => ({ ...prev, governingLaw: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., State of California"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
                <div className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    className="flex-1 px-4 py-2 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                    placeholder="Add a tag"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-500/20 text-blue-300 text-sm rounded-full flex items-center space-x-1"
                    >
                      <span>{tag}</span>
                      <button
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-blue-300 hover:text-white"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Document Parties */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-mockup rounded-xl p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">Document Parties</h2>
              <button
                onClick={addParty}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Party</span>
              </button>
            </div>

            <div className="space-y-4">
              {parties.map((party) => (
                <div key={party.tempId} className="bg-white/5 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="text-lg font-medium text-white">Party {parties.indexOf(party) + 1}</h4>
                    <button
                      onClick={() => removeParty(party.tempId)}
                      className="text-red-400 hover:text-red-300 p-1"
                    >
                      <MinusIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
                      <select
                        value={party.type}
                        onChange={(e) => updateParty(party.tempId, 'type', e.target.value as any)}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      >
                        <option value="client">Client</option>
                        <option value="subcontractor">Subcontractor</option>
                        <option value="vendor">Vendor</option>
                        <option value="employee">Employee</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                      <input
                        type="text"
                        value={party.name}
                        onChange={(e) => updateParty(party.tempId, 'name', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        placeholder="Full name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                      <input
                        type="email"
                        value={party.email}
                        onChange={(e) => updateParty(party.tempId, 'email', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        placeholder="email@example.com"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Role *</label>
                      <input
                        type="text"
                        value={party.role}
                        onChange={(e) => updateParty(party.tempId, 'role', e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        placeholder="e.g., Photographer, Client, Contractor"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={party.signatureRequired}
                          onChange={(e) => updateParty(party.tempId, 'signatureRequired', e.target.checked)}
                          className="rounded border-gray-600 bg-white/5 text-blue-500 focus:ring-blue-500"
                        />
                        <span className="text-gray-300">Signature required from this party</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}

              {parties.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-400 mb-4">No parties added yet.</p>
                  <button
                    onClick={addParty}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>Add First Party</span>
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Save Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex justify-end space-x-4"
          >
            <Link
              to={`/documents/${document.id}`}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Cancel
            </Link>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-600/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </motion.div>
        </div>
      </main>
    </div>
  )
}

export default EditDocumentPage 